import { FunctionComponent, useState, useCallback, ChangeEvent } from 'react'
import { MdPlayArrow } from 'react-icons/md'

import * as Yup from 'yup'

import ConsultZipCodeResponse from 'modules/user/domain/entities/ConsultZipCodeResponse'
import iconEndereco from 'shared/assets/icons/icon-endereco.png'
import Form, { IProps as FormProps } from 'shared/components/Form'
import Input from 'shared/components/Input'
import Page from 'shared/components/Page'
import { colors } from 'shared/styles/colors'
import { cepMask } from 'shared/utils/formatters'

import { steps } from '../../'
import FullAddress from '../FullAddress'
import { ICepAddressProps } from './CepAddressComponent.types'
import { ContainerForm, StyledButton } from './styles'

const CepAddress: FunctionComponent<ICepAddressProps> = ({
  setSelectedStep,
  consultZipCode,
}) => {
  const [errorMessage, setErrorMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [viewFullAddress, setViewFullAddress] = useState(false)
  const [address, setAddress] = useState<ConsultZipCodeResponse>(
    {} as ConsultZipCodeResponse,
  )
  const [cepOk, setCepOk] = useState(
    () => !!localStorage.getItem('@TMov:SignUpCep'),
  )
  const [cep] = useState(() => {
    if (localStorage.getItem('@TMov:SignUpCep')) {
      return localStorage.getItem('@TMov:SignUpCep')
    }

    return ''
  })

  const handleSubmit = useCallback(
    async (values: FormProps['values']) => {
      setLoading(true)
      setErrorMessage('')

      try {
        const response = await consultZipCode.execute({ cep: values.cep })

        if (!response.cep) {
          setErrorMessage('CEP inválido')

          setLoading(false)

          return
        }

        setAddress(response)
        localStorage.setItem('@TMov:SignUpCep', values.cep)
        localStorage.setItem('@TMov:SignUpCidadeIBGE', response.cidade_ibge)
        setViewFullAddress(true)
      } catch (e: unknown) {
        setLoading(false)
        if (e instanceof Error) {
          setErrorMessage(e.message)
        } else {
          setErrorMessage('Erro ao tentar buscar o endereço pelo cep')
        }
      }
    },
    [consultZipCode, setAddress],
  )

  const clearStorageFullAddress = () => {
    localStorage.removeItem('@TMov:SignUpCidade')
    localStorage.removeItem('@TMov:SignUpEstado')
    localStorage.removeItem('@TMov:SignUpLogradouro')
    localStorage.removeItem('@TMov:SignUpNumero')
    localStorage.removeItem('@TMov:SignUpBairro')
    localStorage.removeItem('@TMov:SignUpComplemento')
  }

  const handleChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    setCepOk(false)

    if (!e.target.value) return

    try {
      const schema = Yup.object().shape({
        cep: Yup.string().matches(/^([\d]{5})-*([\d]{3})/),
      })

      await schema.validate(
        {
          cep: e.target.value,
        },
        {
          abortEarly: false,
        },
      )

      clearStorageFullAddress()
      setCepOk(true)
    } catch (e: unknown) {
      setCepOk(false)
    }
  }, [])

  if (viewFullAddress) {
    return <FullAddress address={address} setSelectedStep={setSelectedStep} />
  }

  return (
    <Page
      icon={iconEndereco}
      title="Perfeito!<br />Agora seu endereço:"
      goBackTo={() => {
        setSelectedStep(steps.Photo)
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
        <Form
          onSubmit={handleSubmit}
          initialValues={{ cep }}
          validationSchema={() => ({
            cep: Yup.string().required(),
          })}
        >
          {formControl => (
            <ContainerForm>
              <Input
                placeholder="Digite rua ou CEP para buscar"
                name="cep"
                type="text"
                formControl={formControl}
                errorMessage={errorMessage}
                mask={value => cepMask(value)}
                onChange={handleChange}
                maxLength={9}
              />

              <StyledButton
                color={cepOk ? colors.green : colors.grayLight}
                disabled={!cepOk || loading}
                type="submit"
                loading={loading}
                onClick={formControl.handleSubmit}
              >
                <MdPlayArrow /> Próximo
              </StyledButton>
            </ContainerForm>
          )}
        </Form>
      </div>
    </Page>
  )
}

export default CepAddress
